import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Badge } from "@workspace/amarmeds-ds/components/ui/badge";
import { Button } from "@workspace/amarmeds-ds/components/ui/button";

import { api, type OpsOrder } from "../api";
import { reasonLabel, STATUS_LABEL, taka } from "../format";

const FILTERS: { label: string; statuses?: string }[] = [
  { label: "Open" },
  { label: "Need attention", statuses: "placed,awaiting_customer,payment_failed,delivery_failed" },
  { label: "Delivered", statuses: "delivered" },
  { label: "Rejected / cancelled", statuses: "rejected,cancelled" },
];

// All orders across pharmacies, with what ops needs to phone people. Orders
// the pharmacy has not taken within 10 minutes are flagged.
export default function OpsOrders() {
  const [filter, setFilter] = useState(0);
  const statuses = FILTERS[filter]!.statuses;
  const orders = useQuery({
    queryKey: ["ops-orders", statuses],
    queryFn: () => api<{ orders: OpsOrder[] }>(`/ops/orders${statuses ? `?status=${statuses}` : ""}`),
    refetchInterval: 15_000,
  });
  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Orders</h1>
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f, i) => (
          <Button key={f.label} size="sm" variant={i === filter ? "default" : "outline"} onClick={() => setFilter(i)}>
            {f.label}
          </Button>
        ))}
      </div>
      {orders.isLoading && <p className="text-muted-foreground">Loading…</p>}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="p-2">Order</th>
              <th className="p-2">Status</th>
              <th className="p-2">Age</th>
              <th className="p-2">Total</th>
              <th className="p-2">Pay</th>
              <th className="p-2">Area</th>
              <th className="p-2">Contact</th>
            </tr>
          </thead>
          <tbody>
            {orders.data?.orders.map((o) => {
              const late = o.status === "placed" && o.minutesSincePlaced >= 10;
              return (
                <tr key={o.orderId} className="border-t align-top">
                  <td className="p-2 font-medium">
                    <Link href={`/ops/orders/${o.orderId}`} className="underline">
                      {o.orderId}
                    </Link>
                  </td>
                  <td className="p-2">
                    <Badge variant={late || o.deliveryCodeTries >= 5 ? "destructive" : "secondary"}>{STATUS_LABEL[o.status] ?? o.status}</Badge>
                    {o.reason && <div className="text-xs text-muted-foreground">{reasonLabel(o.reason)}</div>}
                    {o.deliveryCodeTries >= 5 && <div className="text-xs text-destructive">Code locked: call the customer</div>}
                  </td>
                  <td className="p-2">{o.minutesSincePlaced < 120 ? `${o.minutesSincePlaced} min` : `${Math.round(o.minutesSincePlaced / 60)} h`}</td>
                  <td className="p-2">{taka(o.totalPaisa)}</td>
                  <td className="p-2">{o.paymentMethod === "cod" ? "Cash" : "Online"}</td>
                  <td className="p-2">{o.zone}</td>
                  <td className="p-2">
                    {o.phone && <a href={`tel:${o.phone}`}>{o.phone}</a>}
                    {o.address && <div className="max-w-xs text-xs text-muted-foreground">{o.address}</div>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
